import React, { useEffect, useMemo, useState } from 'react';
import { fetchMarketFunding, MarketModuleOffError } from '@/lib/marketExperienceApi';
import {
  awardingEmptyCopy,
  displayFundingEligibility,
  filterFundingAccounts,
  FUNDING_ACCOUNT_SORT_DEFAULTS,
  fundingPitchContext,
  fundingPitchQuestion,
  fundingStageMeta,
  fundingNoDeadlineNote,
  fundingStatusMeta,
  isCategoryRelevantCall,
  isOpenFundingCall,
  isZeroReachCall,
  showWonBeforeBadge,
  sortFundingAccounts,
  urgencyColor,
} from '@/lib/marketFunding';
import { trackSalesMapAction } from '@/components/utils/analytics';
import { useChatSession } from '@/contexts/ChatSessionContext';
import { useUser } from '@/contexts/UserContext';
import { useTranslation } from '@/components/utils/translations';
import MarketHelpLabel, { MarketHelpButton } from './MarketHelpLabel';
import MarketOpportunityDrawer from './MarketOpportunityDrawer';

function formatWhen(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatCount(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString();
}

function formatSecondaryValue(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'number') {
    return new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }).format(value);
  }
  return String(value);
}

function Tile({ label, hint, children }) {
  return (
    <div className="rounded-[10px] border border-[#E5E7EB] bg-white px-4 py-3">
      <p className="text-[9.5px] uppercase tracking-wide text-[#9CA3AF]">
        <MarketHelpLabel label={label} hint={hint} />
      </p>
      <div className="mt-1 text-[19px] font-bold text-[#0A1A2F] leading-snug">{children}</div>
    </div>
  );
}

function SortHeader({ label, sortKey, sort, onSort, hint, align = 'left' }) {
  const { t } = useTranslation();
  const active = sort?.key === sortKey;
  const mark = active ? (sort.direction === 'asc' ? '↑' : '↓') : '';
  const centered = align === 'center';
  return (
    <th
      className={`py-2 pr-3 font-medium ${centered ? 'text-center' : ''}`}
      aria-sort={active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <span className={`inline-flex items-center gap-1 ${centered ? 'w-full justify-center' : ''}`}>
        <button
          type="button"
          className="uppercase tracking-wide hover:text-[#0A1A2F]"
          onClick={() => onSort(sortKey)}
        >
          {label}{mark ? ` ${mark}` : ''}
        </button>
        {hint ? <MarketHelpButton label={t('mktAboutLabel', { label })} hint={hint} /> : null}
      </span>
    </th>
  );
}

function StatusPill({ status }) {
  const meta = fundingStatusMeta(status);
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${meta.className}`}>
      {meta.label}
    </span>
  );
}

export default function MarketFunding({ tenantId, lens, countryFilter }) {
  const { t } = useTranslation();
  const user = useUser();
  const chat = useChatSession();
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unavailable, setUnavailable] = useState(false);
  // Default view is the programmes that NAME one of the tenant's lens categories. A theme-reach
  // programme is discovery, not something a rep can act on, and it outnumbers the direct ones
  // ~4:1 (measured 2026-10-14: 19 theme vs 6 direct on constructor_tech, 28 vs 7 on blackboard).
  // Falls back to 'all' when a lens has no direct programme at all — terminalfour's
  // website_development has 40 calls and ZERO direct, and a default that shows an empty table is
  // worse than the noise it removes. The fallback is applied where the payload arrives.
  const [programmeView, setProgrammeView] = useState('relevant');
  const [drawerRow, setDrawerRow] = useState(null);
  const [accountQuery, setAccountQuery] = useState('');
  const [accountCountry, setAccountCountry] = useState('all');
  const [accountStage, setAccountStage] = useState('all');
  const [accountSort, setAccountSort] = useState(null);

  const country = countryFilter == null || countryFilter === 'all' ? '' : countryFilter;

  useEffect(() => {
    let cancelled = false;
    if (!tenantId || !lens) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setUnavailable(false);
    fetchMarketFunding(tenantId, lens, country)
      .then((data) => {
        if (cancelled) return;
        setPayload(data);
        // Re-pick the default for THIS lens: category-relevant where that is non-empty, otherwise
        // every programme. Done on arrival rather than in a render-time memo so a later click by
        // the rep is never overridden.
        const anyRelevant = (Array.isArray(data?.calls) ? data.calls : [])
          .some((call) => isCategoryRelevantCall(call));
        setProgrammeView(anyRelevant ? 'relevant' : 'all');
      })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof MarketModuleOffError || e?.status === 404) {
          setUnavailable(true);
          setPayload(null);
        } else {
          setError(e?.message || 'Could not load funding');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [tenantId, lens, country]);

  const calls = Array.isArray(payload?.calls) ? payload.calls : [];
  const accounts = Array.isArray(payload?.accounts) ? payload.accounts : [];
  const narrative = Array.isArray(payload?.narrative) ? payload.narrative.filter((p) => String(p || '').trim()) : [];
  const emptyCopy = awardingEmptyCopy(tenantId, payload);
  const visibleCalls = useMemo(() => {
    if (programmeView === 'open') return calls.filter((call) => isOpenFundingCall(call));
    if (programmeView === 'relevant') return calls.filter((call) => isCategoryRelevantCall(call));
    return calls;
  }, [calls, programmeView]);
  // Shown on the toggle so the rep can see what the default is hiding before they click it.
  const relevantCallCount = calls.filter((call) => isCategoryRelevantCall(call)).length;
  const accountCountries = useMemo(() => {
    const names = new Set(accounts.map((row) => row?.country).filter(Boolean));
    return [...names].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [accounts]);
  const visibleAccounts = useMemo(() => {
    const filtered = filterFundingAccounts(accounts, {
      query: accountQuery,
      country: accountCountry,
      stage: accountStage,
    });
    if (!accountSort?.key) return filtered;
    return sortFundingAccounts(filtered, accountSort.key, accountSort.direction);
  }, [accounts, accountQuery, accountCountry, accountStage, accountSort]);

  useEffect(() => {
    if (accountCountry === 'all') return;
    if (!accountCountries.includes(accountCountry)) setAccountCountry('all');
  }, [accountCountries, accountCountry]);

  const onSortAccounts = (key) => {
    setAccountSort((prev) => {
      if (prev?.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: FUNDING_ACCOUNT_SORT_DEFAULTS[key] || 'asc' };
    });
  };

  const pitch = (row) => {
    const ctx = fundingPitchContext(row);
    if (!ctx.programme_name || !chat?.startChatSession) return;
    trackSalesMapAction({
      tenantId,
      userId: user?.id,
      accountId: row.account_id,
      salesPoint: 'market_funding',
      action: 'get_advice',
    }).catch(() => {});
    chat.startChatSession({
      autoSendChatbot: true,
      chatbotRequestId: Date.now(),
      chatbotQuestion: fundingPitchQuestion(ctx.programme_name, row.account_name),
      chatbotContext: ctx,
      account_id: row.account_id,
      tenant_id: tenantId,
      preselectedAccount: {
        account_id: row.account_id,
        account_name: row.account_name,
      },
    });
  };

  if (loading) return <div className="h-64 bg-gray-100 rounded-md animate-pulse" />;
  if (error) {
    return <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error === 'Could not load funding' ? t('mktCouldNotLoadFunding') : error}</div>;
  }
  if (unavailable) {
    return (
      <div className="rounded-[12px] border border-[#E5E7EB] bg-white px-[19px] py-[17px]">
        <p className="text-sm text-[#5C6B7A]">{t('mktFundingNotSwitchedOn')}</p>
      </div>
    );
  }

  const tiles = payload?.tiles || {};
  const eligible = tiles.eligible || {};
  const closing = tiles.closing_soon || {};
  const priority = tiles.also_priority || {};
  const ceiling = tiles.award_ceiling;
  const priorityValue = formatSecondaryValue(priority.value);
  const nearest = formatWhen(closing.nearest_deadline);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Tile
          label={t('mktFundingEligible')}
          hint={t('mktFundingEligibleHint')}
        >
          {formatCount(eligible.accounts)}
          {eligible.of_in_market != null ? (
            <span className="text-[#14B8A6]"> · {t('mktNInMarket', { n: formatCount(eligible.of_in_market) })}</span>
          ) : null}
          {Number(tiles.proven_prior?.same_funder) > 0 ? (
            <span className="text-[#14B8A6]"> · {t('mktNHaveWonBefore', { n: formatCount(tiles.proven_prior.same_funder) })}</span>
          ) : null}
        </Tile>
        <Tile
          label={t('mktClosingSoon')}
          hint={t('mktClosingSoonHint')}
        >
          {formatCount(closing.count)}
          {nearest ? <span className="text-[#14B8A6]"> · {nearest}</span> : null}
        </Tile>
        <Tile
          label={t('mktAwardCeiling')}
          hint={t('mktAwardCeilingHint')}
        >
          <span className="break-words">{ceiling || '—'}</span>
        </Tile>
        <Tile
          label={t('mktAlsoPriority')}
          hint={t('mktAlsoPriorityHint')}
        >
          {formatCount(priority.accounts)}
          {priorityValue ? <span className="text-[#14B8A6]"> · {priorityValue}</span> : null}
        </Tile>
      </div>

      {(narrative.length > 0 || emptyCopy) ? (
        <div className="rounded-[12px] border border-[#E5E7EB] bg-white px-[19px] py-[17px] space-y-2">
          <p className="text-[13.5px] font-semibold text-[#0A1A2F]">
            <MarketHelpLabel
              label={t('mktWhatThisMeans')}
              hint={t('mktWhatThisMeansFundingHint')}
            />
          </p>
          {narrative.map((paragraph, i) => (
            <p key={i} className="text-sm text-[#0A1A2F] leading-relaxed whitespace-pre-wrap">{paragraph}</p>
          ))}
          {emptyCopy ? (
            <p className="text-sm text-[#0A1A2F] leading-relaxed">{emptyCopy}</p>
          ) : null}
        </div>
      ) : null}

      <div className="rounded-[12px] border border-[#E5E7EB] bg-white px-[19px] py-[17px]">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
          <p className="text-[13.5px] font-semibold text-[#0A1A2F]">
            <MarketHelpLabel
              label={t('mktProgrammes')}
              hint={t('mktProgrammesHint')}
            />
          </p>
          <div className="flex items-center gap-2">
            {relevantCallCount > 0 ? (
              <button
                type="button"
                onClick={() => setProgrammeView('relevant')}
                className={`text-[11px] px-3 py-1 rounded-full ${programmeView === 'relevant' ? 'bg-[#14B8A6] text-white' : 'bg-gray-100 text-[#0A1A2F]'}`}
              >
                {t('mktNamesYourCategories', { n: relevantCallCount })}
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => setProgrammeView('all')}
              className={`text-[11px] px-3 py-1 rounded-full ${programmeView === 'all' ? 'bg-[#14B8A6] text-white' : 'bg-gray-100 text-[#0A1A2F]'}`}
            >
              {t('mktAllProgrammes', { n: calls.length })}
            </button>
            <button
              type="button"
              onClick={() => setProgrammeView('open')}
              className={`text-[11px] px-3 py-1 rounded-full ${programmeView === 'open' ? 'bg-[#14B8A6] text-white' : 'bg-gray-100 text-[#0A1A2F]'}`}
            >
              {t('mktOpenRounds')}
            </button>
          </div>
        </div>
        {visibleCalls.length === 0 ? (
          <p className="text-sm text-[#5C6B7A] py-6 text-center">{t('mktNoProgrammesInView')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]">
              <thead className="text-left text-[11px] uppercase tracking-wide text-[#9CA3AF]">
                <tr>
                  <th className="py-2 pr-3 font-medium">{t('mktProgramme')}</th>
                  <th className="py-2 pr-3 font-medium">{t('mktFunder')}</th>
                  <th className="py-2 pr-3 font-medium">{t('mktStatus')}</th>
                  <th className="py-2 pr-3 font-medium">{t('mktAmount')}</th>
                  <th className="py-2 pr-3 font-medium">{t('mktEligibleAccounts')}</th>
                  <th className="py-2 font-medium">{t('mktDeadlineSpendBy')}</th>
                </tr>
              </thead>
              <tbody>
                {visibleCalls.map((call) => {
                  const open = isOpenFundingCall(call);
                  const deadline = open ? formatWhen(call.deadline) : null;
                  const days = open && call.days_remaining != null ? Number(call.days_remaining) : null;
                  // An active programme has no deadline — nobody applies to it — but it does have a
                  // published spend-by date. Showing '—' hid a real date on every World Bank row.
                  const spendBy = !deadline && call.spend_window_end
                    ? formatWhen(call.spend_window_end)
                    : null;
                  // An ESTIMATED window is ours, not the funder's. `duration_inferred` is the
                  // lane's word for a modelled date, and the API sends the basis so this column
                  // can say which kind of date it is showing — "spend by" asserts a published
                  // condition, which would be wrong on a forecast.
                  const spendEstimated =
                    String(call.spend_window_basis || '') === 'duration_inferred';
                  const dim = isZeroReachCall(call);
                  return (
                    <tr
                      key={call.call_slug || call.programme_name}
                      className={`border-t border-[#E5E7EB] ${dim ? 'opacity-50' : ''}`}
                    >
                      <td className="py-2 pr-3 align-top font-medium text-[#0A1A2F]">
                        {call.source_url ? (
                          <a
                            href={call.source_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:text-[#14B8A6] hover:underline"
                          >
                            {call.programme_name || '—'}
                          </a>
                        ) : (call.programme_name || '—')}
                      </td>
                      <td className="py-2 pr-3 align-top text-[#5C6B7A]">{call.funder || '—'}</td>
                      <td className="py-2 pr-3 align-top"><StatusPill status={call.status} /></td>
                      <td className="py-2 pr-3 align-top">{call.amount_text || '—'}</td>
                      <td className="py-2 pr-3 align-top">{formatCount(call.eligible_accounts)}</td>
                      <td className="py-2 align-top">
                        {deadline ? (
                          <span>
                            {deadline}
                            {Number.isFinite(days) ? (
                              <span className="block text-[10.5px]" style={{ color: urgencyColor(days) }}>
                                {t('mktNDays', { n: days })}
                              </span>
                            ) : null}
                          </span>
                        ) : spendBy ? (
                          <span>
                            {spendBy}
                            <span
                              className="block text-[10.5px]"
                              style={{ color: call.spend_window_open === false ? '#9CA3AF' : '#5C6B7A' }}
                              title={
                                call.spend_window_open === false
                                  ? undefined
                                  : spendEstimated
                                    ? t('mktSpendEstimateTitle')
                                    : undefined
                              }
                            >
                              {call.spend_window_open === false
                                ? t('mktWindowClosed')
                                : spendEstimated ? t('mktEstSpendBy') : t('mktSpendBy')}
                            </span>
                          </span>
                        ) : (() => {
                          const note = fundingNoDeadlineNote(call);
                          if (!note) return <span className="text-[#9CA3AF]">—</span>;
                          return (
                            <span className="text-[#5C6B7A]">
                              {note.date
                                ? t(note.key, { date: formatWhen(note.date) })
                                : t(note.key)}
                              {note.sub ? (
                                <span className="block text-[10.5px] text-[#9CA3AF]">{t(note.sub)}</span>
                              ) : null}
                            </span>
                          );
                        })()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="rounded-[12px] border border-[#E5E7EB] bg-white px-[19px] py-[17px]">
        <p className="text-[13.5px] font-semibold text-[#0A1A2F] mb-3">
          <MarketHelpLabel
            label={t('accounts')}
            hint={t('mktFundingAccountsHint')}
          />
        </p>
        <div className="flex flex-wrap items-center gap-2 mb-3">
          {[
            ['all', 'All'],
            ['open_round', 'Open round'],
            ['prior_award', 'Already funded'],
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setAccountStage(key)}
              className={`text-xs px-3 py-1 rounded-full ${accountStage === key ? 'bg-[#14B8A6] text-white' : 'bg-gray-100 text-[#0A1A2F]'}`}
            >
              {key === 'all' ? t('all') : key === 'open_round' ? t('mktOpenRound') : key === 'prior_award' ? t('mktAlreadyFunded') : label}
            </button>
          ))}
        </div>
        <div className="flex flex-col sm:flex-row gap-2 mb-3">
          <input
            type="search"
            value={accountQuery}
            onChange={(e) => setAccountQuery(e.target.value)}
            placeholder={t('mktSearchByAccountName')}
            aria-label={t('mktSearchByAccountName')}
            className="h-9 flex-1 rounded-md border border-[#E5E7EB] bg-white px-3 text-sm text-[#0A1A2F] placeholder:text-[#9CA3AF]"
          />
          <select
            value={accountCountry}
            onChange={(e) => setAccountCountry(e.target.value)}
            aria-label={t('filterByCountry')}
            className="h-9 rounded-md border border-[#E5E7EB] bg-white px-2 text-sm text-[#0A1A2F]"
          >
            <option value="all">{t('mktAllCountries')}</option>
            {accountCountries.map((name) => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
        </div>
        {accounts.length === 0 ? (
          <p className="text-sm text-[#5C6B7A] py-6 text-center">{t('mktNoAccountsInView')}</p>
        ) : visibleAccounts.length === 0 ? (
          <p className="text-sm text-[#5C6B7A] py-6 text-center">{t('mktNoAccountsMatchSearch')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-[11px] text-[#9CA3AF]">
                <tr>
                  <SortHeader label={t('account')} sortKey="account" sort={accountSort} onSort={onSortAccounts} />
                  <SortHeader
                    label={t('mktTotalFundingEligibility')}
                    sortKey="eligibility"
                    sort={accountSort}
                    onSort={onSortAccounts}
                    align="center"
                    hint={t('mktTotalFundingEligibilityHint')}
                  />
                  <SortHeader label={t('country')} sortKey="country" sort={accountSort} onSort={onSortAccounts} align="center" />
                  <SortHeader label={t('mktStanding')} sortKey="standing" sort={accountSort} onSort={onSortAccounts} align="center" />
                  <SortHeader label={t('mktStage')} sortKey="stage" sort={accountSort} onSort={onSortAccounts} align="center" />
                  <SortHeader label={t('fit')} sortKey="fit" sort={accountSort} onSort={onSortAccounts} align="center" />
                  <SortHeader label={t('mktInYourMarket')} sortKey="in_market" sort={accountSort} onSort={onSortAccounts} align="center" />
                  <th className="py-2 font-medium" />
                </tr>
              </thead>
              <tbody>
                {visibleAccounts.map((row) => {
                  const ctx = fundingPitchContext(row);
                  return (
                    <tr key={row.account_id || row.account_name} className="border-t border-[#E5E7EB]">
                      <td className="py-2.5 pr-3 align-top">
                        <button
                          type="button"
                          className="font-medium text-[#0A1A2F] hover:text-[#14B8A6] text-left"
                          onClick={() => setDrawerRow(row)}
                        >
                          {row.account_name || t('account')}
                        </button>
                        {showWonBeforeBadge(row) ? (
                          <span className="ml-2 inline-flex items-center rounded-full bg-[#DCFCE7] text-[#166534] px-2 py-0.5 text-[11px] font-medium">
                            {t('mktWonBefore')}
                          </span>
                        ) : null}
                      </td>
                      <td className="py-2.5 pr-3 align-middle text-center tabular-nums whitespace-nowrap">
                        {displayFundingEligibility(row.total_funding_eligibility) || <span className="text-[#9CA3AF]">—</span>}
                        {row.open_programme_count > 1 ? (
                          <span className="block text-xs text-[#5C6B7A] tabular-nums">
                            {t('mktAcrossNProgrammes', { n: row.open_programme_count })}
                          </span>
                        ) : null}
                      </td>
                      <td className="py-2.5 pr-3 align-middle text-center text-[#5C6B7A]">{row.country || '—'}</td>
                      <td className="py-2.5 pr-3 align-middle text-center">{row.standing_label || '—'}</td>
                      <td className="py-2.5 pr-3 align-middle text-center">
                        {fundingStageMeta(row.funding_basis) ? (
                          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${fundingStageMeta(row.funding_basis).className}`}>
                            {fundingStageMeta(row.funding_basis).label}
                          </span>
                        ) : (
                          <span className="text-[#9CA3AF]">—</span>
                        )}
                      </td>
                      <td className="py-2.5 pr-3 align-middle text-center">{row.market_score ?? '—'}</td>
                      <td className="py-2.5 pr-3 align-middle text-center">{row.in_sam ? t('mktYes') : t('mktNo')}</td>
                      <td className="py-2.5 align-top text-right">
                        {ctx.programme_name ? (
                          <button
                            type="button"
                            className="text-[#14B8A6] font-medium whitespace-nowrap hover:underline"
                            onClick={() => pitch(row)}
                          >
                            {t('mktHowToPitch')}
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <MarketOpportunityDrawer
        tenantId={tenantId}
        lens={lens}
        accountId={drawerRow?.account_id}
        seedRow={drawerRow}
        open={Boolean(drawerRow)}
        onOpenChange={(open) => { if (!open) setDrawerRow(null); }}
      />
    </div>
  );
}
